import { useMemo } from 'react'
import { NotKnown } from './bits'
import type { ProjectSummary } from '../lib/db'
import { money } from '../lib/paper'
import {
  board,
  columnOf,
  NOT_ON_THE_BOARD,
  nextStep,
  type BoardJob,
  type JobColumn,
} from '../lib/jobsBoard'

/**
 * THE JOBS BOARD.
 *
 * JOB-01, held back on purpose until the events behind it existed. lib/leads
 * has carried the reason since the day it was written: four of six columns
 * would have been permanently empty, and a board with empty columns is a board
 * that lies about the shape of the work.
 *
 * NOTHING HERE DRAGS. A column is worked out from what happened rather than
 * stored, so there is no column to set. Each card carries the real thing that
 * moves it instead, and that thing always happens on another screen: approve a
 * scope, book a visit, press start. That is not a limitation of the board, it
 * is the only way a board can be true.
 */
export default function JobsBoard({
  items,
  onOpenJob,
  onGo,
}: {
  items: ProjectSummary[]
  onOpenJob: (job: ProjectSummary) => void
  /** The board knows what should happen next; the workspace knows where. */
  onGo: (where: 'price' | 'scope' | 'schedule' | 'today' | 'job', job: ProjectSummary) => void

}) {
  const cols = useMemo(() => {
    const jobs: BoardJob[] = items.map((j) => ({
      id: j.id,
      customer: j.customer,
      address: j.address,
      name: j.name,
      status: j.status,
      scopeApproved: j.work?.approved ?? false,
      total: j.money?.total ?? 0,
      balance: j.money?.balance ?? 0,
      visits: j.visits ?? [],
    }))
    return board(jobs)
  }, [items])

  const byId = useMemo(() => new Map(items.map((j) => [j.id, j])), [items])
  const who = (j: BoardJob) => j.customer.trim() || j.address.trim() || j.name

  return (
    <>
      <div className="jb">
        {cols.map((c) => (
          <section key={c.id} className={`jbcol ${c.id}`}>
            <header>
              <p className="jbcol-name">
                {c.label}
                <em>{c.jobs.length}</em>
              </p>
              {/*
                THE VALUE ONLY WHERE MONEY IS THE POINT. A column of houses
                nobody has priced has no value, and printing a nought under it
                reads as a business doing badly rather than as a photograph
                somebody took on Saturday.
              */}
              {c.value > 0 && <span className="jbcol-val">{money(c.value)}</span>}
              <span className="jbcol-blurb">{c.blurb}</span>
            </header>

            {c.jobs.length === 0 ? (
              <p className="jbcol-none">Nothing here.</p>
            ) : (
              c.jobs.map((j) => {
                const step = nextStep(j)
                const full = byId.get(j.id)
                return (
                  <div key={j.id} className="jbcard">
                    <button className="jbcard-open" onClick={() => full && onOpenJob(full)}>
                      <strong>{who(j)}</strong>
                      {j.address.trim() && who(j) !== j.address.trim() && (
                        <span>{j.address}</span>
                      )}
                      {j.total > 0 && (
                        <span className="jbcard-money">
                          {money(j.total)}
                          {j.balance > 0 && j.balance !== j.total && (
                            <em>{money(j.balance)} owed</em>
                          )}
                        </span>
                      )}
                    </button>
                    {step && (
                      <div className="jbcard-next">
                        <p>{step.does}</p>
                        <button
                          className="btn"
                          onClick={() => full && onGo(step.goes, full)}
                        >
                          {step.label}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </section>
        ))}
      </div>

      <NotKnown title="What this board does not do" items={NOT_ON_THE_BOARD} />
    </>
  )
}

export { columnOf }
export type { JobColumn }
